import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Contacts from './components/Contacts/Contacts';
import AdminPanel from './components/Admin/AdminPanel';
import TasksPage from './components/Tasks/TasksPage';
import NetworkTree from './components/Network/NetworkTree';
import ChallengesList from './components/Challenges/ChallengesList/ChallengesList';
import ChallengeDetail from './components/Challenges/ChallengeDetail/ChallengeDetail';
import CreateChallengeModal from './components/Challenges/CreateChallengeModal/CreateChallengeModal';
import CalendarPage from './components/Calendar/CalendarPage';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [showCreateChallengeModal, setShowCreateChallengeModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is logged in on app load (skip for public pages)
  useEffect(() => {
    const publicPaths = ['/login', '/register'];
    const currentPath = window.location.pathname;
    
    if (!publicPaths.includes(currentPath)) {
      checkAuth();
    } else {
      // On public pages, just set loading to false without checking auth
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/backend/api/auth/me.php', {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAdmin(data.user.role === 'admin');
      }
      // 401 is expected when user is not logged in - don't treat as error
    } catch (error) {
      // This will only catch actual network errors, not HTTP status codes
      // 401 responses don't throw exceptions, they just have response.status === 401
      console.warn('Network error during auth check:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAdmin(userData.role === 'admin');
  };

  const handleLogout = async () => {
    try {
      await fetch('/backend/api/auth/logout.php', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setIsAdmin(false);
    }
  };

  const switchView = (view, data) => {
    console.log('[App] switchView called with:', { view, data, currentView });
    
    if (view === 'create-challenge') {
      console.log('[App] Opening create challenge modal');
      setShowCreateChallengeModal(true);
    } else if (view === 'challenges') {
      console.log('[App] Setting currentView to challenges');
      setCurrentView('challenges');
      setSelectedChallengeId(data || null);
    } else if (view === 'challenge-detail') {
      console.log('[App] Setting currentView to challenge-detail');
      setCurrentView('challenge-detail');
      setSelectedChallengeId(data);
    } else {
      console.log('[App] Setting currentView to:', view);
      setCurrentView(view);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route 
            path="/login" 
            element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />} 
          />
          <Route 
            path="/register" 
            element={user ? <Navigate to="/" /> : <Register onLogin={handleLogin} />} 
          />
          <Route 
            path="/register/:referralCode" 
            element={user ? <Navigate to="/" /> : <Register onLogin={handleLogin} />} 
          />
          <Route 
            path="/" 
            element={
              user ? (
                <>
                  {currentView === 'dashboard' && 
                    <Dashboard user={user} onLogout={handleLogout} onNavigate={switchView} isAdmin={isAdmin} />}
                  {currentView === 'contacts' &&
                    <Contacts user={user} onLogout={handleLogout} onNavigate={switchView} />}
                  {currentView === 'network' &&
                    <NetworkTree user={user} onNavigate={switchView} />}
                  {currentView === 'tasks' &&
                    <TasksPage user={user} onBack={() => switchView('dashboard')} />}
                  {(currentView === 'challenges' && console.log('[App] Rendering ChallengesList, user:', user)) || null}
                  {currentView === 'challenges' &&
                    <ChallengesList onNavigate={switchView} initialChallengeId={selectedChallengeId} user={user} />}
                  {currentView === 'challenge-detail' && selectedChallengeId &&
                    <ChallengeDetail challengeId={selectedChallengeId} onNavigate={switchView} />}
                  {currentView === 'admin' && isAdmin &&
                    <AdminPanel user={user} onNavigate={switchView} />}
                  {!['dashboard', 'contacts', 'network', 'tasks', 'challenges', 'challenge-detail', 'admin'].includes(currentView) &&
                    <Dashboard user={user} onLogout={handleLogout} onNavigate={switchView} isAdmin={isAdmin} />}
                  <CreateChallengeModal 
                    isOpen={showCreateChallengeModal} 
                    onClose={() => setShowCreateChallengeModal(false)}
                    onSuccess={() => {
                      setShowCreateChallengeModal(false);
                      switchView('challenges');
                    }}
                  />
                </>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route
            path="/calendar"
            element={user ? <CalendarPage /> : <Navigate to="/login" />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
